import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage, fetchPayout, savePayout, type PayoutResponse } from "../services/expensesService";
import { money, monthLabel } from "../lib/format";
import { ui } from "../theme";
import { Chip, ErrorBox, Notice } from "./ui";

// Cuadre de la dispersión de nómina (NVIO): lo transferido a la cuenta, cada
// envío convertido con el tipo de cambio de su día, contra lo que marca el
// padrón para quienes se pagan por ahí. Solo totales: el sueldo de cada quien
// sigue en Equipo.

const STATUS_TONE: Record<string, string> = {
  cuadra: "bg-emerald-500/15 text-emerald-600",
  de_mas: "bg-amber-500/15 text-amber-600",
  de_menos: "bg-red-500/15 text-red-600",
  sin_tipo_de_cambio: "bg-slate-500/15 text-slate-500",
  sin_nomina: "bg-slate-500/15 text-slate-500",
  sin_envio: "bg-slate-500/15 text-slate-500",
};

export default function PayoutCard() {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const [data, setData] = useState<PayoutResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [account, setAccount] = useState("");
  const [label, setLabel] = useState("NVIO");
  const [selected, setSelected] = useState<string[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const apply = (d: PayoutResponse) => {
    setData(d);
    setAccount(d.config?.account || "");
    setLabel(d.config?.label || "NVIO");
    setSelected(d.config?.memberIds || []);
  };

  useEffect(() => {
    fetchPayout()
      .then(apply)
      .catch((err) => setError(apiErrorMessage(err) || t("expenses.errors.load")));
  }, [t]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      apply(await savePayout({ account, label, memberIds: selected }));
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.save"));
    } finally {
      setSaving(false);
    }
  };

  if (!data && !error) return null;
  const r = data?.reconciliation;
  const cur = r?.currency || "USD";
  const lang = i18n.language;

  return (
    <div className={c.card}>
      <h3 className={`mb-1 text-sm font-semibold ${c.text}`}>{t("expenses.payout.title", { label: data?.config?.label || label })}</h3>
      <p className={`mb-3 text-xs ${c.muted}`}>{t("expenses.payout.hint")}</p>
      <ErrorBox message={error} />

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className={`text-xs ${c.muted}`}>
          <span className="block">{t("expenses.payout.account")}</span>
          <input id="payout-account" inputMode="numeric" className={`${c.inputBase} mt-1 w-56`} value={account} onChange={(e) => setAccount(e.target.value)} />
        </label>
        <label className={`text-xs ${c.muted}`}>
          <span className="block">{t("expenses.payout.label")}</span>
          <input id="payout-label" className={`${c.inputBase} mt-1 w-32`} value={label} onChange={(e) => setLabel(e.target.value)} />
        </label>
        <fieldset className={`text-xs ${c.muted}`}>
          <legend className="mb-1">{t("expenses.payout.members")}</legend>
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {(data?.candidates || []).map((m) => (
              <label key={m.id} className={`inline-flex items-center gap-1.5 ${c.text}`}>
                <input
                  type="checkbox"
                  checked={selected.includes(m.id)}
                  onChange={(e) => setSelected((s) => (e.target.checked ? [...s, m.id] : s.filter((x) => x !== m.id)))}
                />
                {m.name} <span className={c.muted}>{m.currency}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <button type="button" className={c.secondary} disabled={saving || !account.trim()} onClick={save}>
          {t("expenses.payout.save")}
        </button>
      </div>

      {!r && <Notice tone="info">{t("expenses.payout.notConfigured")}</Notice>}
      {r && r.months.length === 0 && <Notice tone="info">{t("expenses.payout.noTransfers")}</Notice>}
      {r && r.months.length > 0 && (
        <>
          {r.monthsWithoutTransfer.length > 0 && (
            <Notice tone="warn">{t("expenses.payout.monthsWithoutTransfer", { months: r.monthsWithoutTransfer.map((p) => monthLabel(p, lang)).join(", ") })}</Notice>
          )}
          <p className={`mb-2 text-xs ${c.muted}`}>
            {t("expenses.payout.covers", { names: r.members.map((m) => m.name).join(", ") || "—", pct: Math.round(r.tolerance * 100) })}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`text-left text-xs ${c.muted}`}>
                  <th className="py-1">{t("expenses.summary.month")}</th>
                  <th className="py-1 text-right">{t("expenses.payout.sent")}</th>
                  <th className="py-1 text-right">{t("expenses.payout.sentForeign", { currency: cur })}</th>
                  <th className="py-1 text-right">{t("expenses.payout.expected", { currency: cur })}</th>
                  <th className="py-1 text-right">{t("expenses.payout.diff")}</th>
                  <th className="py-1">{t("expenses.fields.status")}</th>
                </tr>
              </thead>
              <tbody>
                {r.months.map((m) => (
                  <Fragment key={m.period}>
                    <tr className={`cursor-pointer border-t ${c.border} ${c.text} ${c.rowHover}`} onClick={() => setOpen(open === m.period ? null : m.period)}>
                      <td className="py-1.5">
                        <i className={`${open === m.period ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"} mr-1`} />
                        {monthLabel(m.period, lang)}
                        <span className={`ml-1 text-xs ${c.muted}`}>· {m.transfers.length}</span>
                      </td>
                      <td className="py-1.5 text-right tabular-nums">{money(m.sent, "MXN", lang)}</td>
                      <td className="py-1.5 text-right tabular-nums">{m.sentForeign == null ? "—" : money(m.sentForeign, cur, lang)}</td>
                      <td className="py-1.5 text-right tabular-nums">{m.expectedForeign == null ? "—" : money(m.expectedForeign, cur, lang)}</td>
                      <td className="py-1.5 text-right tabular-nums">
                        {m.diffForeign == null ? "—" : `${m.diffForeign > 0 ? "+" : ""}${money(m.diffForeign, cur, lang)}`}
                        {m.diffPct != null && <span className={`ml-1 text-xs ${c.muted}`}>({(m.diffPct * 100).toFixed(1)}%)</span>}
                      </td>
                      <td className="py-1.5">
                        <Chip className={STATUS_TONE[m.status]}>{t(`expenses.payout.status.${m.status}`)}</Chip>
                      </td>
                    </tr>
                    {open === m.period &&
                      m.transfers.map((tr) => (
                        <tr key={tr.id} className={`text-xs ${c.muted}`}>
                          <td className="py-1 pl-6 tabular-nums">{tr.date}</td>
                          <td className="py-1 text-right tabular-nums">{money(tr.amount, "MXN", lang)}</td>
                          <td className="py-1 text-right tabular-nums">{tr.foreign == null ? "—" : money(tr.foreign, cur, lang)}</td>
                          <td className="py-1 text-right tabular-nums" colSpan={2}>
                            {tr.rate == null ? t("expenses.payout.noRate") : t("expenses.payout.rate", { rate: tr.rate.toFixed(4) })}
                          </td>
                          <td />
                        </tr>
                      ))}
                  </Fragment>
                ))}
                <tr className={`border-t ${c.border} font-semibold ${c.text}`}>
                  <td className="py-1.5">{t("expenses.kpi.total")}</td>
                  <td className="py-1.5 text-right tabular-nums">{money(r.totals.sent, "MXN", lang)}</td>
                  <td className="py-1.5 text-right tabular-nums">{r.totals.sentForeign == null ? "—" : money(r.totals.sentForeign, cur, lang)}</td>
                  <td className="py-1.5 text-right tabular-nums">{r.totals.expectedForeign == null ? "—" : money(r.totals.expectedForeign, cur, lang)}</td>
                  <td className="py-1.5 text-right tabular-nums">{r.totals.diffForeign == null ? "—" : `${r.totals.diffForeign > 0 ? "+" : ""}${money(r.totals.diffForeign, cur, lang)}`}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
